import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useRequestPoolController } from 'hooks/useRequestPoolController';
import { fetchCampaignGroupsList } from 'modules/campaigns/modules/campaigns-group/flows';
import { campaignsGroupMapSelector } from 'modules/campaigns/modules/campaigns-group/selectors';
import { useAccount } from 'modules/taboola-common-frontend-modules/account-configurations';
import { useCampaignGroupApi } from 'services/api';

export const useCampaignGroupsFetchService = () => {
    const dispatch = useDispatch();
    const { accountId: accountName } = useAccount();
    const campaignsGroupMap = useSelector(campaignsGroupMapSelector);
    const fetchCampaignGroupsFlow = useCallback(params => dispatch(fetchCampaignGroupsList(params)), [dispatch]);
    const setRequestPoolDependencies = useRequestPoolController();
    const campaignGroupApi = useCampaignGroupApi();

    const loadCampaignGroups = useCallback(
        async ({ page, pageSize, searchText, apiCacheKey }) => {
            if (!accountName) {
                return [[], 0];
            }

            const wasDependenciesChanged = setRequestPoolDependencies(accountName, page, pageSize, searchText);
            const allowMultiRequestCache = !wasDependenciesChanged;

            const [results, total] = await fetchCampaignGroupsFlow({
                accountName,
                page,
                pageSize,
                searchText,
                apiCacheKey,
                allowMultiRequestCache,
            });
            return [results, total];
        },
        [accountName, fetchCampaignGroupsFlow, setRequestPoolDependencies]
    );

    const loadCampaignGroupsTotals = useCallback(async () => {
        try {
            const { metadata } = await campaignGroupApi.getCampaignGroupList(accountName, {
                page: 1,
                pageSize: 1,
                searchText: '',
            });
            return metadata.total;
        } catch (e) {
            return 0;
        }
    }, [accountName, campaignGroupApi]);

    const getFullCampaignsGroup = useCallback(
        campaignsGroup => campaignsGroup && campaignsGroupMap[campaignsGroup.id],
        [campaignsGroupMap]
    );

    return { loadCampaignGroups, loadCampaignGroupsTotals, getFullCampaignsGroup };
};
